import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginHrmsUser } from "../../../Redux/slice/features/authSlice";
import { REACT_APP_API_URL } from "../../../env";
import { validateWeakPassword } from "../../../utils/passwordValidator";
import {
  getLockoutStatus,
  registerFailure,
  clearLockout,
  formatRemainingMinutes,
} from "./lockout";

const SigninHrms = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading } = useSelector((state) => state.auth);
  const GENERIC_LOGIN_ERROR = "Invalid credentials";

  const [formData, setFormData] = useState({
    employeeID: "",
    password: "",
  });

  const [captchaText, setCaptchaText] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");
  const [toast, setToast] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lockoutInfo, setLockoutInfo] = useState(() =>
    getLockoutStatus("hrms", "")
  );
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    specialChar: false,
    capitalLetter: false,
  });

  const identifier = useMemo(
    () => formData.employeeID.trim().toLowerCase(),
    [formData.employeeID]
  );

  const loadCaptcha = async () => {
    try {
      const res = await fetch(`${REACT_APP_API_URL}api/auth/hrms/captcha`, {
        credentials: "include",
      });
      const data = await res.json();

      setCaptchaText(data.captcha);
      setCaptchaId(data.captchaId);
      setCaptchaValue("");
    } catch {
      setToast("Unable to load captcha");
      setTimeout(() => setToast(""), 4000);
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Track password requirements in real-time
    if (name === "password") {
      setPasswordRequirements({
        minLength: value.length >= 8,
        specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value),
        capitalLetter: /[A-Z]/.test(value),
      });
    }
  };

  useEffect(() => {
    setLockoutInfo(getLockoutStatus("hrms", identifier));
  }, [identifier]);

  const preventCopyPasteAndShortcuts = (e) => {
    const blockedKey = ["c", "v", "x"].includes(e.key?.toLowerCase());
    if ((e.ctrlKey || e.metaKey) && blockedKey) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const lockoutStatus = getLockoutStatus("hrms", identifier);
    if (lockoutStatus.isLocked) {
      setToast(
        `Too many failed attempts. Try again in ${formatRemainingMinutes(
          lockoutStatus.remainingMs
        )}.`
      );
      setIsSubmitting(false);
      return;
    }

    if (!captchaId || !captchaValue) {
      setToast("Please enter captcha");
      setTimeout(() => setToast(""), 4000);
      setIsSubmitting(false);
      return;
    }

    // Validate password policy in sign in page not required 
    // const passwordValidation = validateWeakPassword(formData.password);
    // if (!passwordValidation.isValid) {
    //   setToast(passwordValidation.errors.join("\n"));
    //   setTimeout(() => setToast(""), 5000);
    //   setIsSubmitting(false);
    //   return;
    // }

    try {
      const res = await dispatch(
        loginHrmsUser({
          employee_id: formData.employeeID,
          password: formData.password,
          captchaId,
          captchaValue: captchaValue.trim(),
        })
      );

      if (res.meta.requestStatus === "rejected") {
        const isCaptchaError = /captcha/i.test(res?.payload || "");
        if (!isCaptchaError) {
          const updated = registerFailure("hrms", identifier);
          setLockoutInfo(updated);
          if (updated.isLocked) {
            setToast(
              `Too many failed attempts. Account locked for ${formatRemainingMinutes(
                updated.remainingMs
              )}.`
            );
          } else {
            setToast(
              `${GENERIC_LOGIN_ERROR}. ${updated.attemptsLeft} attempt${updated.attemptsLeft === 1 ? "" : "s"
              } left.`
            );
          }
        } else {
          setToast(res.payload);
        }

        loadCaptcha();
        setTimeout(() => setToast(""), 5000);
        return;
      }

      clearLockout("hrms", identifier);
      setLockoutInfo(getLockoutStatus("hrms", identifier));
      sessionStorage.setItem("hrmsUser", JSON.stringify(res.payload));
      sessionStorage.setItem("hrmsLogin", "true");

      navigate("/hrms", { replace: true });
    } catch {
      const updated = registerFailure("hrms", identifier);
      setLockoutInfo(updated);
      if (updated.isLocked) {
        setToast(
          `Too many failed attempts. Account locked for ${formatRemainingMinutes(
            updated.remainingMs
          )}.`
        );
      } else {
        setToast(
          `${GENERIC_LOGIN_ERROR}. ${updated.attemptsLeft} attempt${updated.attemptsLeft === 1 ? "" : "s"
          } left.`
        );
      }
      loadCaptcha();
      setTimeout(() => setToast(""), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto redirect if logged in
  useEffect(() => {
    const isSessionActive = sessionStorage.getItem("hrmsLogin") === "true";
    if (isSessionActive && location.pathname !== "/hrms") {
      navigate("/hrms", { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="px-xl-5 px-4 auth-body">
      <form onSubmit={handleSubmit} autoComplete="off">
        <ul className="row g-3 list-unstyled li_animate">
          <h1 className="mb-4 text-center display-9 fw-bold text-primary">
            HRMS Login
          </h1>

          <li className="col-12">
            <label className="form-label">Employee ID</label>
            <input
              type="text"
              name="employeeID"
              className="form-control form-control-lg"
              placeholder="Enter your employeeID"
              value={formData.employeeID}
              onChange={handleChange}
              autoComplete="off"
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onKeyDown={preventCopyPasteAndShortcuts}
              required
            />
          </li>

          <li className="col-12">
            <div className="form-label">Password</div>
            <div className="input-group">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="form-control form-control-lg"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="off"
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onKeyDown={preventCopyPasteAndShortcuts}
                required
              />
              <button
                type="button"
                className="btn btn-light border shadow-none"
                tabIndex={-1}
                onClick={() => setShowPassword((prev) => !prev)}
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                {showPassword ? (
                  <i className="bi bi-eye-slash"></i>
                ) : (
                  <i className="bi bi-eye"></i>
                )}
              </button>
            </div>
            
            {/* Password Requirements Display not needed for signin */}
           
          </li>

          <li className="col-12">
            <label className="form-label">Captcha</label>

            <div className="input-group input-group-lg">
              <span
                className="input-group-text fw-bold bg-light"
                style={{
                  letterSpacing: "6px",
                  fontFamily: "monospace",
                  userSelect: "none",
                }}
              >
                {captchaText}
              </span>

              <button
                type="button"
                className="btn"
                onClick={loadCaptcha}
                title="Refresh Captcha"
              >
                ↻
              </button>

              <input
                type="text"
                className="form-control text-center"
                placeholder="Enter captcha"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value)}
                autoComplete="off"
                required
              />
            </div>
          </li>

          <li className="col-12 my-lg-4">
            <button
              type="submit"
              className="btn btn-lg w-100 btn-primary text-uppercase mb-2"
              disabled={loading || isSubmitting || lockoutInfo.isLocked}
            >
              {loading ? "Signing in..." : "SIGN IN"}
            </button>

            {identifier && !lockoutInfo.isLocked && (
              <div className="text-muted small text-center">
                {lockoutInfo.attemptsLeft} attempt{lockoutInfo.attemptsLeft === 1 ? "" : "s"} left
              </div>
            )}

            {lockoutInfo.isLocked && (
              <div className="alert alert-warning mt-2 text-center">
                Account locked. Try again in {formatRemainingMinutes(lockoutInfo.remainingMs)}.
              </div>
            )}

            <Link
              to="/login"
              onClick={() => {
                sessionStorage.removeItem("hrmsLogin");
                sessionStorage.removeItem("adminLogin");
                // resetCaptcha();
              }}
              className="d-block mt-2 text-decoration-underline text-primary"
            >
              Login as Admin/Manager
            </Link>
          </li>

          {toast && (
            <div
              className={`alert mt-2 text-center ${/captcha/i.test(toast) ? "alert-warning" : "alert-danger"
                }`}
              style={{ minWidth: "300px" }}
            >
              {toast}
            </div>
          )}
        </ul>
      </form>
    </div>
  );
};

export default SigninHrms;
